import { supabase } from '../../lib/supabase';
import { ApiError } from '../../lib/errors';
import type { Analysis } from '../../types/models';

export async function getAnalysis(analysisId: string): Promise<Analysis> {
  const { data, error } = await supabase
    .from('analysis')
    .select(`
      *,
      candidates (
        name,
        cv_text
      ),
      jobs (
        title,
        jd_text
      )
    `)
    .eq('id', analysisId)
    .maybeSingle();

  if (error) {
    throw new ApiError('Analysis not found', 404);
  }

  return {
    ...data,
    cv_summary: data.candidates?.cv_text || '',
    job_requirements: data.jobs?.jd_text || '',
    interview_perspectives: data.interview_perspectives || {},
    selected_perspective: data.selected_perspective || 'manager'
  };
}

export async function listAnalyses(): Promise<Analysis[]> {
  // Get current user
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    // Handle case where user is not logged in - return empty array or throw error
    // Returning empty array for graceful handling in UI
    console.warn('listAnalyses called without authenticated user.');
    return [];
  }

  // Get user's organization ID
  const { data: member } = await supabase
    .from('organization_members')
    .select('organization_id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (!member?.organization_id) {
    // Return empty array if user has no organization yet
    return [];
  }

  const { data, error } = await supabase
    .from('analysis')
    .select(`
      *,
      candidates (
        name
      ),
      jobs (
        title
      )
    `)
    .eq('organization_id', member.organization_id) // Filter by organization ID
    .order('created_at', { ascending: false })
    .limit(10); // Limit to the latest 10 analyses

  if (error) {
    console.error('Failed to fetch analyses:', error); // Log error for debugging
    throw new ApiError(`Failed to fetch analyses: ${error.message}`, 500);
  }

  return data?.map(analysis => ({
    ...analysis,
    cv_summary: '',
    job_requirements: '',
    interview_perspectives: analysis.interview_perspectives || {},
    selected_perspective: analysis.selected_perspective || 'manager'
  })) || [];
}

export async function updateAnalysisNotes(analysisId: string, notes: string): Promise<void> {
  const { error } = await supabase
    .from('analysis')
    .update({ notes })
    .eq('id', analysisId);

  if (error) {
    throw new ApiError('Failed to update notes', 500);
  }
}